import React, { useCallback } from "react";
import { Grid } from "@mui/material";
import { FormField, RadioButtonGroup } from "../shared/form";
import { MisdeliveryFormData } from "../../models/misDelivery";
import Location from "./../../screens/overageEntry/Location";
import {
  CURRENT_LOCATION_FORM_OBJECT,
  LOCATION_FORM_OBJECT,
  M_GRID_PROPS,
  S_GRID_PROPS
} from "../../constants";
import { useMisdeliveryFormContext } from "../../contexts/MisdeliveryFormContext";
import NoteBox from "../noteBox/NoteBox";

interface MisdeliveryInformationSectionProps {
  osdDetails?: any;
}

// Helper: Check if field has error
const hasFieldError = (
  touched: Record<string, boolean | undefined>,
  errors: Record<string, string | undefined>,
  fieldName: string,
): boolean => {
  return !!(touched[fieldName] && errors[fieldName]);
};

// Helper: Get field helper text
const getFieldHelperText = (
  touched: Record<string, boolean | undefined>,
  errors: Record<string, string | undefined>,
  fieldName: string,
): string | undefined => {
  return touched[fieldName] ? errors[fieldName] : undefined;
};

const MisdeliveryInformationSection: React.FC<
  MisdeliveryInformationSectionProps
> = ({ osdDetails }) => {
  // Get form state from context
  const {
    formData,
    errors,
    touched,
    onFieldChange,
    handleChange,
    handleBlur,
    disabled,
    setFieldTouched,
  } = useMisdeliveryFormContext();

  const clearCurrentLocationTouched = useCallback(() => {
    const fieldsToClear: (keyof MisdeliveryFormData)[] = [
      "currentLocationAddress1",
      "currentLocationAddress2",
      "currentLocationCity",
      "currentLocationZip",
      "currentLocationState",
      "pickupTerminal",
    ];

    fieldsToClear.forEach((field) => {
      // Clear touched so any existing validation messages hide immediately
      // when values are auto-populated or reset.
      setFieldTouched(field, false, false);
    });
  }, [setFieldTouched]);

  const resetCurrentLocationFields = useCallback(() => {
    const currentAddressFields: (keyof MisdeliveryFormData)[] = [
      "currentLocationAddress1", "currentLocationAddress2", "currentLocationCity", "currentLocationZip", "currentLocationState", "pickupTerminal"
    ];
    currentAddressFields.forEach((field) => {
      onFieldChange(field as keyof MisdeliveryFormData, "");
    });
    clearCurrentLocationTouched();
  }, [onFieldChange, clearCurrentLocationTouched]);

  const populateCurrentLocationFromMisdelivery = useCallback(() => {
    onFieldChange("currentLocationAddress1", formData?.misdeliveryAddress1);
    onFieldChange("currentLocationAddress2", formData?.misdeliveryAddress2);
    onFieldChange("currentLocationCity", formData?.misdeliveryCity);
    onFieldChange("currentLocationState", formData?.misdeliveryState);
    onFieldChange("currentLocationZip", formData?.misdeliveryZip);
    onFieldChange("pickupTerminal", formData?.destinationTerminal);
    clearCurrentLocationTouched();
  }, [onFieldChange, formData, clearCurrentLocationTouched]);

  // Field change handler for RadioButtonGroup
  const handleFieldChange = useCallback(
    (fieldName: keyof MisdeliveryFormData) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const nextValue = e.target.value;
        onFieldChange(fieldName, nextValue);

        if (fieldName !== "currentLocationDifferentThanMsdPoint") return;

        if (nextValue === "Y") {
          resetCurrentLocationFields();
        } else if (nextValue === "N") {
          populateCurrentLocationFromMisdelivery();
        }
      },
    [onFieldChange, resetCurrentLocationFields, populateCurrentLocationFromMisdelivery],
  );

  const hasProNumber = formData.hasProNumber === "Y";
  const isHasProSelected = ["Y", "N"].includes(formData.hasProNumber ?? "");
  const isMisdeliveryFieldsDisabled = !isHasProSelected || hasProNumber || disabled;

  // Helper to render FormField with common props
const renderFormField = useCallback(
  (
    id: keyof MisdeliveryFormData,
    label: string,
    placeholder: string,
    options: {
      maxLength?: number;
      required?: boolean;
      type?: "number" | "text" | "email";
      disabled?: boolean;
      gridProps?: any;
      error?: boolean;
      helperText?: string;
    } = {}
  ) => {
    const value = formData[id] ?? "";
    const errorMsg = errors[id];
    const isRequiredError = errorMsg?.includes("Required") && formData[id];
    const showError = touched[id] && errorMsg && !isRequiredError;

    return (
      <FormField
        id={id}
        label={label}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        maxLength={options.maxLength}
        required={options.required}
        type={options.type}
        disabled={options.disabled}
        error={options.error ?? !!showError}
        helperText={
          options.helperText ?? (showError ? errorMsg : undefined)
        }
        gridProps={options.gridProps}
      />
    );
  },
  [formData, touched, errors, handleChange, handleBlur]
);

  const memoizedFormik = React.useMemo(
    () => ({
      handleBlur,
      setFieldValue: onFieldChange,
      errors,
      touched,
      values: formData,
      setFieldTouched,
    }),
    [handleBlur, onFieldChange, errors, touched, formData, setFieldTouched],
  );

  const isContactInfoTouched =
    (touched.callerPhone || touched.callerEmail) ?? false; /* NOSONAR */

  const isContactInfoEmpty = !formData.callerEmail && !formData.callerPhone;

  const contactInfoError =
    isContactInfoTouched && isContactInfoEmpty
      ? "At least one of Phone #, or Email is required"
      : undefined;

  const lockCurrentLocationFields =
    formData.currentLocationDifferentThanMsdPoint === "N";

  return (
    <>
      <h3 className="accordion-content-header">Misdelivery Information</h3>

      <Grid container spacing={2} className="filter-margin">
        {renderFormField(
          "misdeliveryContactName",
          "Misdelivery's Contact Name",
          "Enter Contact Name",
          {
            maxLength: 30,
            required: true,
            disabled: isMisdeliveryFieldsDisabled,
            gridProps: S_GRID_PROPS,
          },
        )}
        {renderFormField(
          "misdeliveryAddress1",
          "Misdelivery Address 1",
          "Enter Address 1",
          {
            maxLength: 30,
            required: true,
            disabled: isMisdeliveryFieldsDisabled,
            gridProps: S_GRID_PROPS,
          },
        )}

        {renderFormField(
          "misdeliveryAddress2",
          "Misdelivery Address 2",
          "Enter Address 2",
          {
            maxLength: 30,
            disabled: isMisdeliveryFieldsDisabled,
            gridProps: S_GRID_PROPS,
          },
        )}

        {hasProNumber && (
          <>
            {renderFormField("misdeliveryCity", "City", "Enter City", {
              maxLength: 20,
              required: true,
              disabled: hasProNumber || disabled,
              gridProps: S_GRID_PROPS,
            })}
            {renderFormField("misdeliveryState", "State", "Enter State", {
              maxLength: 2,
              required: true,
              disabled: hasProNumber || disabled,
              gridProps: S_GRID_PROPS,
            })}
            {renderFormField("misdeliveryZip", "Zipcode", "Enter Zipcode", {
              maxLength: 6,
              required: true,
              disabled: hasProNumber || disabled,
              gridProps: S_GRID_PROPS,
            })}
          </>
        )}
        {formData.hasProNumber !== "Y" && (
          <Location
            key={formData.misdeliveryState} // Force remount when hasProNumber changes to reset internal state
            osdDetails={osdDetails}
            formFieldsDisabled={isMisdeliveryFieldsDisabled}
            formik={memoizedFormik}
            formObject={LOCATION_FORM_OBJECT}
            required={true}
          />
        )}
        {renderFormField(
          "misdeliveryContactPhone",
          "Misdelivery Contact Number",
          "Enter Contact Number",
          {
            maxLength: 10,
            required: true,
            type: "number",
            gridProps: S_GRID_PROPS,
            disabled,
          },
        )}

        {renderFormField(
          "misdeliveryContactEmail",
          "Misdelivery Contact Email Address",
          "Enter Email Address",
          {
            maxLength: 40,
            required: true,
            type: "email",
            gridProps: S_GRID_PROPS,
            disabled,
          },
        )}

        {renderFormField(
          "callerName",
          "Caller/Requesting Party Name (First, Last)",
          "Enter First Name",
          {
            maxLength: 30,
            required: true,
            gridProps: S_GRID_PROPS,
            disabled,
          },
        )}

        {renderFormField(
          "callerPhone",
          "Caller/Requesting Party Phone #",
          "Enter Phone #",
          {
            maxLength: 10,
            required: true,
            type: "number",
            gridProps: S_GRID_PROPS,
            error: !!contactInfoError,
            helperText: contactInfoError,
            disabled,
          },
        )}
        {renderFormField(
          "callerExt",
          "Caller/Requesting Party Ext",
          "Enter Ext",
          {
            maxLength: 6,
            type: "number",
            required: false,
            disabled,
            gridProps: S_GRID_PROPS,
          },
        )}

        {renderFormField(
          "callerEmail",
          "Caller/Requesting Party Email Address",
          "Enter Email Address",
          {
            maxLength: 40,
            required: true,
            gridProps: S_GRID_PROPS,
            error:
              !!contactInfoError ||
              !!(touched.callerEmail && errors.callerEmail),
            helperText: contactInfoError,
            disabled,
          },
        )}
        {hasProNumber && (
          <>
          <RadioButtonGroup
            id="currentLocationDifferentThanMsdPoint"
            label="Current location of the freight if different than the misdelivery point"
            value={formData.currentLocationDifferentThanMsdPoint}
            onChange={handleFieldChange("currentLocationDifferentThanMsdPoint")}
            onBlur={handleBlur}
            required
            error={hasFieldError(touched, errors, "currentLocationDifferentThanMsdPoint")}
            helperText={getFieldHelperText(
              touched,
              errors,
              "currentLocationDifferentThanMsdPoint",
            )}
            gridProps={S_GRID_PROPS}
            disabled={disabled}
          />
            {renderFormField(
              "currentLocationAddress1",
              "Address 1",
              "Enter  Address  1",
              {
                maxLength: 30,
                required: true,
                gridProps: S_GRID_PROPS,
                disabled: disabled || lockCurrentLocationFields,
              },
            )}
            {renderFormField(
              "currentLocationAddress2",
              "Address 2",
              "Enter  Address  2",
              {
                maxLength: 30,
                gridProps: S_GRID_PROPS,
                disabled: disabled || lockCurrentLocationFields,
              },
            )}
            <Location
              osdDetails={osdDetails}
              key={formData.currentLocationState}
              formFieldsDisabled={disabled || lockCurrentLocationFields}
              formik={memoizedFormik}
              formObject={CURRENT_LOCATION_FORM_OBJECT}
              required={true}
            />

            {renderFormField(
              "currentContactName",
              "Current contact name at MSD location of freight",
              "Enter current contact name at MSD location of freight",
              {
                maxLength: 30,
                required: true,
                gridProps: M_GRID_PROPS,
                disabled,
              },
            )}
            {renderFormField(
              "currentContactPhone",
              "Phone #",
              "Enter Phone #",
              {
                maxLength: 10,
                type: "number",
                gridProps: S_GRID_PROPS,
                disabled,
              },
            )}
            {renderFormField("currentContactExt", " Ext", "Enter Ext", {
              maxLength: 6,
              type: "number",
              gridProps: S_GRID_PROPS,
              disabled: disabled,
            })}

            {renderFormField(
              "currentContactEmail",
              "Email Address",
              "Enter Email Address",
              {
                maxLength: 40,
                gridProps: S_GRID_PROPS,
                disabled,
              },
            )}
          </>
        )}
        <RadioButtonGroup
          id="shipmentDeliveredOutsideUS"
          label="Shipment Delivered Outside US?"
          value={formData.shipmentDeliveredOutsideUS}
          onChange={handleFieldChange("shipmentDeliveredOutsideUS")}
          onBlur={handleBlur}
          required
          error={hasFieldError(touched, errors, "shipmentDeliveredOutsideUS")}
          helperText={getFieldHelperText(
            touched,
            errors,
            "shipmentDeliveredOutsideUS",
          )}
          gridProps={M_GRID_PROPS}
          disabled={disabled}
        />
        {formData.shipmentDeliveredOutsideUS === "Y" && (
          <> 
          <Grid item {...M_GRID_PROPS} className="mt-10">
           
          </Grid>
          <Grid item {...M_GRID_PROPS} className="mt-10">
            <NoteBox message="OSDCLP@SAIA.COM approval required before terminal can schedule an MSD pickup." />
          </Grid>
          </>
        )}
      </Grid>
    </>
  );
};

export default MisdeliveryInformationSection;
